import { useEffect, useRef, useState } from 'react';
import { magnitude } from '../lib/format';
import { C, ink, surface, text } from '../lib/theme';
import { InfoDot, Skeleton } from './primitives';

/**
 * Anima o número de um valor ao outro (~400ms) em vez de trocar seco — PRD §6.
 * Respeita `prefers-reduced-motion`: quem pediu menos movimento recebe o valor
 * final direto, sem contagem.
 */
export function useCountUp(target: number, duration = 420): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number>();

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { fromRef.current = target; setValue(target); return; }

    const from = fromRef.current;
    if (from === target) return;
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + (target - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return value;
}

/**
 * Sparkline (PRD §9-D): mini-linha da série recente, contexto sem virar
 * gráfico. `height` variável — o card-hero da Visão Geral usa uma versão
 * bem mais alta que o mini-gráfico padrão dos KPI Cards.
 */
export function Sparkline({ points, color, height = 26 }: { points: number[]; color: string; height?: number }) {
  if (points.length < 2) return <div style={{ height }} />;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const span = max - min || 1;
  const w = 100, h = height;
  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${(i / (points.length - 1)) * w},${h - ((p - min) / span) * h}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block', overflow: 'visible' }}>
      <path d={`${d} L${w},${h} L0,${h} Z`} fill={color} opacity={0.1} />
      <path d={d} fill="none" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/**
 * KPI Card (PRD §6): chip de ícone + rótulo com tooltip de fórmula + sublabel +
 * número grande animado + sparkline. Clicar ancora na seção correspondente.
 */
export function KpiCard({ icon, label, formula, value, format, sub, color, glow, chipBg, spark, sparkColor, loading, onClick }: {
  /** Ícone SVG de `components/icons.tsx` — não glifo de texto (ver o porquê lá). */
  icon: React.ReactNode;
  label: string;
  formula: string;
  value: number;
  format: (n: number) => string;
  sub?: string;
  color?: string;
  glow?: string;
  chipBg?: string;
  spark?: number[];
  sparkColor?: string;
  loading?: boolean;
  onClick?: () => void;
}) {
  const animated = useCountUp(value);

  if (loading) return <Skeleton height={132} radius={22} />;

  return (
    <div
      onClick={onClick}
      className="ko-kpi"
      style={{
        ...surface, padding: '18px 20px 16px', position: 'relative',
        cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
        <span style={{
          width: 40, height: 40, borderRadius: 13, flex: 'none',
          background: chipBg ?? ink(0.08), color: color ?? ink(0.7),
          border: `1px solid ${color ? `${color}38` : ink(0.1)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: glow ? `0 0 18px -3px ${color ?? C.accent}66` : 'none',
        }}>{icon}</span>
        <span style={{
          fontSize: 11.5, fontWeight: 800, letterSpacing: '0.11em', textTransform: 'uppercase',
          color: text.title, minWidth: 0,
        }}>{label}</span>
        <InfoDot title={formula} />
      </div>

      <div style={{
        fontSize: 33, fontWeight: 800, letterSpacing: '-0.035em', fontVariantNumeric: 'tabular-nums',
        color: color ?? C.fg, textShadow: glow ?? 'none', lineHeight: 1.05,
      }}>{format(animated)}</div>

      {sub && <div style={{ fontSize: 13, color: text.body, marginTop: 6 }}>{sub}</div>}

      {spark && spark.length > 1 && (
        <div style={{ marginTop: 10 }}>
          <Sparkline points={spark.map(magnitude)} color={sparkColor ?? color ?? C.accent} />
        </div>
      )}
    </div>
  );
}
